import { useMemo, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import {
  Box,
  Collapse,
  Skeleton,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import { REALADVISOR_PORTAL_ID } from '@realadvisor/listings';
import { endOfDay, format, parseISO, startOfDay, subDays } from 'date-fns';
import { ChevronRight } from 'lucide-react';

import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { formatNumber } from '@modules/_shared/utils/formatting';
import { LISTING_FUNNEL } from '@modules/listings/listingActivityQueries';
import { useLocale } from '@shared/hooks/locale';

import { computeBuyerFunnelMetrics } from './buyerFunnelMetrics';
import { ChartRangeControls, InfoTip } from './ChartControls';
import type { Granularity } from './chartUtils';
import { ListingBuyerLeadsBody } from './ListingBuyerLeadsChart';
import {
  type Callout,
  CoachCallouts,
  type CoachSection,
} from './ListingCoachCallouts';
import { ListingEnquiriesBody } from './ListingEnquiriesChart';
import { ListingImpressionsBody } from './ListingImpressionsCard';
import { ListingOffersBody } from './ListingOffersChart';
import { ListingViewingsBody } from './ListingViewingsChart';
import { shouldShowPortalSteps } from './portalStepsVisibility';
import { useListingImpressions } from './useListingImpressions';

// Funnel steps that expand into an inline trend chart. Impressions and detail
// views share the impressions chart (different metric pre-selected); enquiries
// and buyer leads each have their own.
export type FunnelDrillKey = 'impressions' | 'enquiries' | 'buyerLeads';

const STEP_COLORS = {
  impressions: '#4285f4',
  views: '#c2185b',
  enquiries: '#00897b',
  buyerLeads: '#5e35b1',
  viewings: '#e8710a',
  offers: '#3949ab',
} as const;

type StepKey = keyof typeof STEP_COLORS;

type FunnelStep = {
  key: StepKey;
  label: string;
  help: string;
  count: number;
  color: string;
  // Which coach callouts surface inside this step when expanded.
  alertSection?: CoachSection;
  expandable: boolean;
  // GA4-backed steps (impressions / listing views) render a skeleton while the
  // listing → GA4 mapping and stats resolve.
  loading?: boolean;
  unavailable?: boolean;
};

// Window used for the impressions / listing-views top of the funnel. Real GA4
// counts are scanned per day-shard, so we cap the lifetime summary to a year.
const IMPRESSIONS_WINDOW_DAYS = 365;

// The DB funnel steps are a lifetime summary, so they start at the epoch.
const FUNNEL_FROM = new Date(0).toISOString();

export const ListingFunnel = ({
  lotId,
  callouts,
  onDismissCallout,
}: {
  lotId: string;
  callouts: Callout[];
  onDismissCallout: (id: string) => void;
}) => {
  const { t, locale, countryCode } = useLocale();

  // Impressions and listing views come from the RealAdvisor portal, which only
  // exists in Switzerland today. Elsewhere they would always read 0, so the
  // funnel starts at enquiries and we skip the GA4 lookup entirely.
  const hasPortal = countryCode === 'CH';

  const [expanded, setExpanded] = useState<Set<StepKey>>(new Set());
  // `null` until the agent toggles the card themselves; before that the card
  // opens or collapses based on whether the funnel has any activity.
  const [cardExpanded, setCardExpanded] = useState<boolean | null>(null);
  const [granularity, setGranularity] = useState<Granularity>('day');
  const today = useMemo(() => startOfDay(new Date()), []);
  const [range, setRange] = useState<[Date, Date]>([subDays(today, 27), today]);
  const [start, end] = range;

  const impressionsStart = useMemo(
    () => subDays(today, IMPRESSIONS_WINDOW_DAYS - 1),
    [today],
  );
  const {
    totalImpressions,
    totalViews,
    loading: impressionsLoading,
    error: impressionsError,
  } = useListingImpressions(lotId, impressionsStart, today, 'day', {
    skip: !hasPortal,
  });

  // All-time real counts: the funnel is a lifetime summary; the per-step charts
  // below are scoped to the selected range.
  const funnelTo = useMemo(() => endOfDay(today).toISOString(), [today]);
  const funnelAsOfDate = useMemo(() => format(today, 'yyyy-MM-dd'), [today]);
  const { data, loading: funnelLoading } = useQuery(LISTING_FUNNEL, {
    variables: {
      lotId,
      from: FUNNEL_FROM,
      to: funnelTo,
      realadvisorPortalId: REALADVISOR_PORTAL_ID,
    },
  });

  // Even on a CH tenant the portal steps only mean something when the listing
  // was on RealAdvisor while impressions were being tracked: never published,
  // or taken down before the data starts, both read 0 for the wrong reason.
  const showPortalSteps =
    hasPortal &&
    shouldShowPortalSteps({
      portalListings: data?.realadvisor_portal_listings ?? [],
      windowStart: impressionsStart,
      impressionsLoading,
      totalImpressions,
      totalViews,
    });

  // Skeleton the whole funnel until every drawn source has resolved. The DB
  // counts (LISTING_FUNNEL) arrive before the GA4 impressions/views, and showing
  // the DB bars early makes them visibly rescale once impressions land
  // (impressions sets the bar scale), so we wait for both before drawing any
  // real bar. When the portal steps are hidden, impressions never set the scale,
  // so the GA4 lookup (fired before we know the lot's publication state) must
  // not hold the rest of the funnel back.
  const allLoading = funnelLoading || (showPortalSteps && impressionsLoading);

  const buyerMetrics = useMemo(
    () =>
      computeBuyerFunnelMetrics(
        (data?.lot_buyer_leads ?? []).map(buyerLead => {
          const viewedAt =
            buyerLead.successful_viewings.aggregate?.min?.due_at ?? null;
          return {
            userId: buyerLead.user_id,
            firstSuccessfulViewingDate:
              viewedAt == null
                ? null
                : format(parseISO(viewedAt), 'yyyy-MM-dd'),
          };
        }),
        (data?.lot_offers ?? []).map(offer => ({
          buyerId: offer.buyer_id,
          madeOn: offer.made_on,
        })),
        funnelAsOfDate,
      ),
    [data, funnelAsOfDate],
  );

  const steps: FunnelStep[] = useMemo(() => {
    const enquiries = data?.enquiries.aggregate?.count ?? 0;
    const portalSteps: FunnelStep[] = [
      {
        key: 'impressions',
        label: t('Impressions on RealAdvisor'),
        help:
          impressionsError != null
            ? t('Failed to load data')
            : t(
                'How many times this listing was shown on RealAdvisor in the last 365 days.',
              ),
        count: totalImpressions,
        color: STEP_COLORS.impressions,
        alertSection: 'impressions',
        expandable: impressionsError == null,
        loading: allLoading,
        unavailable: impressionsError != null,
      },
      {
        key: 'views',
        label: t('Listing views on RealAdvisor'),
        help:
          impressionsError != null
            ? t('Failed to load data')
            : t(
                "How many times the listing's detail page was opened on RealAdvisor in the last 365 days.",
              ),
        count: totalViews,
        color: STEP_COLORS.views,
        expandable: impressionsError == null,
        loading: allLoading,
        unavailable: impressionsError != null,
      },
    ];
    return [
      ...(showPortalSteps ? portalSteps : []),
      {
        key: 'enquiries',
        label: t('Enquiries'),
        help: t('Contact requests received for this listing.'),
        count: enquiries,
        color: STEP_COLORS.enquiries,
        alertSection: 'enquiries',
        expandable: true,
        loading: allLoading,
      },
      {
        key: 'buyerLeads',
        label: t('Buyer leads'),
        help: t('Buyers linked to this listing, by source'),
        count: buyerMetrics.buyerLeads,
        color: STEP_COLORS.buyerLeads,
        alertSection: 'buyerLeads',
        expandable: true,
        loading: allLoading,
      },
      {
        key: 'viewings',
        label: t('Viewed buyers'),
        help: t(
          'Unique buyers with at least one completed successful viewing.',
        ),
        count: buyerMetrics.viewedBuyers,
        color: STEP_COLORS.viewings,
        expandable: true,
        loading: allLoading,
      },
      {
        key: 'offers',
        label: t('Buyers with offers'),
        help: t(
          'Unique viewed buyers who made an offer on or after their first successful viewing.',
        ),
        count: buyerMetrics.buyersWithOffers,
        color: STEP_COLORS.offers,
        alertSection: 'offers',
        expandable: true,
        loading: allLoading,
      },
    ];
  }, [
    data,
    buyerMetrics,
    totalImpressions,
    totalViews,
    impressionsError,
    allLoading,
    showPortalSteps,
    t,
  ]);

  // An all-zero funnel is noise for a listing that has had no activity yet, so
  // the card collapses itself once the counts land. It stays open while
  // loading (skeletons), when a step failed to load (so the error is visible
  // rather than one click away) and defers to the agent once they toggle it.
  const hasActivity = steps.some(step => step.count > 0);
  const hasUnavailable = steps.some(step => step.unavailable === true);
  const isCardExpanded =
    cardExpanded ?? (allLoading || hasActivity || hasUnavailable);

  // Bars are scaled against the largest loaded step. Impressions normally set
  // the scale, while the fallback keeps relative DB counts meaningful when a
  // listing has no GA4 mapping.
  const maxCount = Math.max(...steps.map(step => step.count), 1);

  const toggle = (key: StepKey) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  const renderBody = (key: StepKey) => {
    switch (key) {
      case 'impressions':
        return (
          <ListingImpressionsBody
            lotId={lotId}
            start={start}
            end={end}
            granularity={granularity}
            metric="impressions"
          />
        );
      case 'views':
        return (
          <ListingImpressionsBody
            lotId={lotId}
            start={start}
            end={end}
            granularity={granularity}
            metric="views"
          />
        );
      case 'enquiries':
        return (
          <ListingEnquiriesBody
            lotId={lotId}
            start={start}
            end={end}
            granularity={granularity}
          />
        );
      case 'buyerLeads':
        return (
          <ListingBuyerLeadsBody
            lotId={lotId}
            start={start}
            end={end}
            granularity={granularity}
          />
        );
      case 'viewings':
        return (
          <ListingViewingsBody
            start={start}
            end={end}
            granularity={granularity}
            viewedBuyerDates={buyerMetrics.viewedBuyerDates}
            color={STEP_COLORS.viewings}
            loading={funnelLoading}
          />
        );
      case 'offers':
        return (
          <ListingOffersBody
            lotId={lotId}
            start={start}
            end={end}
            granularity={granularity}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ListingAccordion
      title={t('Conversion funnel')}
      expanded={isCardExpanded}
      onChange={(_, isExpanded) => setCardExpanded(isExpanded)}
    >
      <Stack spacing={1}>
        {steps.map((step, index) => {
          const prev = index > 0 ? steps[index - 1] : null;
          // Enquiries and buyer leads are not a strict sequence: matching and
          // manual buyer leads can exist without an enquiry. The buyer-lead,
          // viewed-buyer and offered-buyer steps are one nested buyer cohort.
          const hasComparableConversion =
            (prev?.key === 'impressions' && step.key === 'views') ||
            (prev?.key === 'buyerLeads' && step.key === 'viewings') ||
            (prev?.key === 'viewings' && step.key === 'offers');
          const conversion =
            hasComparableConversion &&
            prev != null &&
            prev.count > 0 &&
            !step.loading &&
            prev.loading !== true &&
            step.unavailable !== true &&
            prev.unavailable !== true
              ? step.count / prev.count
              : null;
          const widthPct = Math.max(3, (step.count / maxCount) * 100);
          const isOpen = expanded.has(step.key);
          const sectionCallouts =
            step.alertSection != null
              ? callouts.filter(c => c.section === step.alertSection)
              : [];

          return (
            <Box key={step.key}>
              <Box
                {...(step.expandable && {
                  role: 'button',
                  tabIndex: 0,
                  onClick: () => toggle(step.key),
                  onKeyDown: (event: React.KeyboardEvent) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggle(step.key);
                    }
                  },
                })}
                sx={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  borderRadius: 1.5,
                  p: 0.75,
                  transition: 'background-color 0.15s ease',
                  ...(step.expandable && {
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha(step.color, 0.06) },
                  }),
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {step.expandable && (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          color: 'text.disabled',
                          transition: 'transform 0.15s ease',
                          transform: isOpen ? 'rotate(90deg)' : 'none',
                        }}
                      >
                        <ChevronRight size={14} />
                      </Box>
                    )}
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: 'text.primary' }}
                    >
                      {step.label}
                    </Typography>
                    <InfoTip title={step.help} />
                    {sectionCallouts.length > 0 && (
                      <Box
                        component="span"
                        title={t('{{count}} insight needs attention', {
                          count: sectionCallouts.length,
                        })}
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: 'warning.main',
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    {conversion != null && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary' }}
                      >
                        {`${(conversion * 100).toLocaleString(locale, {
                          maximumFractionDigits: conversion < 0.1 ? 1 : 0,
                        })}%`}
                      </Typography>
                    )}
                    {step.loading === true ? (
                      <Skeleton
                        variant="text"
                        width={44}
                        sx={{ fontSize: '0.875rem' }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {step.unavailable === true
                          ? '—'
                          : formatNumber(step.count, locale)}
                      </Typography>
                    )}
                  </Box>
                </Box>
                {step.loading === true ? (
                  <Skeleton
                    variant="rounded"
                    height={12}
                    sx={{ borderRadius: 6 }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 12,
                      borderRadius: 6,
                      bgcolor: 'action.hover',
                      overflow: 'hidden',
                    }}
                  >
                    {step.unavailable !== true && (
                      <Box
                        sx={{
                          height: '100%',
                          width: `${widthPct}%`,
                          borderRadius: 6,
                          bgcolor: step.color,
                          transition: 'width 0.3s ease',
                        }}
                      />
                    )}
                  </Box>
                )}
              </Box>
              {step.expandable && (
                <Collapse in={isOpen} unmountOnExit>
                  <Box
                    sx={{
                      mt: 1,
                      mb: 0.5,
                      p: 1.5,
                      borderRadius: 1.5,
                      border: 1,
                      borderColor: 'divider',
                      bgcolor: 'background.default',
                    }}
                  >
                    <Stack spacing={1.5}>
                      {sectionCallouts.length > 0 && (
                        <CoachCallouts
                          callouts={sectionCallouts}
                          onDismiss={onDismissCallout}
                        />
                      )}
                      <ChartRangeControls
                        start={start}
                        end={end}
                        granularity={granularity}
                        onGranularity={setGranularity}
                        onRange={(from, to) => setRange([from, to])}
                      />
                      {renderBody(step.key)}
                    </Stack>
                  </Box>
                </Collapse>
              )}
            </Box>
          );
        })}
      </Stack>
    </ListingAccordion>
  );
};
